import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            url: `https://example.com/article/${article.slug}`,
            inLanguage: 'zh-Hans',
            wordCount: article.content.length,
            author: {
              '@type': 'Organization',
              name: '应用安全指南',
            },
            publisher: {
              '@type': 'Organization',
              name: '应用安全指南',
              url: 'https://example.com/',
            },
            image: article.image,
            keywords: article.tags.join(', '),
            articleSection: category?.name || '',
          }),
        }}
      />

      <Navbar />
      <div className="pt-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: category ? `/category/${category.slug}` : undefined },
              { label: article.title },
            ]}
          />

          <article className="py-10">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-primary-100 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{article.date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-500">阅读时间 {article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-64 md:h-80 object-cover"
              />
            </div>

            <div
              className="article-content bg-white rounded-lg p-6 md:p-10 border border-background-200/70"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-8">
              <span className="text-xs text-foreground-500 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <div className="pb-14">
              <h3 className="text-lg font-semibold text-foreground-950 mb-5">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-white rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}